import React from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { Button } from '../../../interaxDesignSystemComponents'
import { TESTIDS } from '../../../config/testids'
import { PublicRoutes } from '../../../config/routes/routeEnums'

export interface CookieConsentBannerProps {
  onAccept: () => void
  onReject: () => void
}

/**
 * CookieConsentBanner Component
 *
 * GDPR-compliant cookie consent banner that appears at the bottom of the page
 * Shows on first visit until user accepts or rejects cookies
 */
export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onAccept, onReject }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault()
    navigate(PublicRoutes.PRIVACY)
  }

  return (
    <Box
      component="div"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-message"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: INTERAX_COLORS.offBlack,
        borderTop: `1px solid ${INTERAX_COLORS.white20}`,
        borderRadius: { xs: '12px 12px 0 0', md: '12px 12px 0 0' },
        padding: { xs: '16px', md: '24px' },
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: '16px', md: '24px' },
        maxWidth: { xs: '100%', md: '1200px' },
        margin: { xs: '0', md: '0 auto' }
      }}
    >
      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <Typography
          id="cookie-consent-title"
          sx={{
            color: INTERAX_COLORS.white,
            fontFamily: INTERAX_TYPOGRAPHY.headline6.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.headline6.fontWeight,
            fontSize: { xs: '16px', md: INTERAX_TYPOGRAPHY.headline6.fontSize },
            lineHeight: INTERAX_TYPOGRAPHY.headline6.lineHeight,
            mb: 0.5
          }}
        >
          {t('landing.cookieConsent.title')}
        </Typography>
        <Typography
          id="cookie-consent-message"
          sx={{
            color: INTERAX_COLORS.typographyFont,
            fontFamily: INTERAX_TYPOGRAPHY.body2.fontFamily,
            fontWeight: INTERAX_TYPOGRAPHY.body2.fontWeight,
            fontSize: { xs: '14px', md: INTERAX_TYPOGRAPHY.body2.fontSize },
            lineHeight: INTERAX_TYPOGRAPHY.body2.lineHeight
          }}
        >
          {t('landing.cookieConsent.message')}{' '}
          <Box
            component="a"
            href={PublicRoutes.PRIVACY}
            onClick={handlePrivacyClick}
            sx={{
              color: INTERAX_COLORS.sun,
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: INTERAX_COLORS.sun20,
                opacity: 0.8
              }
            }}
          >
            {t('landing.cookieConsent.privacyNoticeLink')}
          </Box>
          .
        </Typography>
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '12px', md: '16px' },
          width: { xs: '100%', md: 'auto' },
          minWidth: { xs: '100%', md: '200px' }
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            minWidth: { xs: '100%', md: '120px' }
          }}
        >
          <Button
            variant="secondary"
            onClick={onReject}
            data-testid={TESTIDS.COOKIE_REJECT_BTN}
            size="medium"
            fullWidth={true}
            style={{
              width: '100%'
            }}
          >
            {t('landing.cookieConsent.reject')}
          </Button>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: 'auto' },
            minWidth: { xs: '100%', md: '120px' }
          }}
        >
          <Button
            variant="primary"
            onClick={onAccept}
            data-testid={TESTIDS.COOKIE_ACCEPT_BTN}
            size="medium"
            fullWidth={true}
            style={{
              width: '100%'
            }}
          >
            {t('landing.cookieConsent.accept')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default CookieConsentBanner
